import type {
	DraftEmissionRequestFormData,
	EmissionValueAnsweredUnit,
	EmissionValueUnit,
	FormTemplateResponse,
	ProductCreateRequest,
} from '@api-client'
import { InfoIcon } from '@assets/icons'
import { DatePickerController } from '@components/controllers'
import { FormUnitSelector } from '@components/FormUnitSelector'
import { Input } from '@components/Input'
import {
	type AutocompleteOptions,
	SupplierAutocomplete,
} from '@components/SupplierAutocomplete'
import { ProductReferenceTooltip } from '@components/Tooltips'
import {
	Box,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material'
import { useCallback } from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type UseFormClearErrors,
	useFormContext,
	type UseFormGetValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type ProductType = Omit<ProductCreateRequest, 'supplierLinks' | 'unit'> & {
	supplierId?: string
	requestComment?: string
	emissionData?: DraftEmissionRequestFormData
	unit: EmissionValueUnit | EmissionValueAnsweredUnit
	purchasingPeriodStart?: string
	purchasingPeriodEnd?: string
	formTemplateId?: string
}

interface Props {
	hasRequestCommentField?: boolean
	displaySupplierSelection?: boolean
	control: Control<ProductType>
	supplierData?: readonly AutocompleteOptions[]
	disabled?: boolean
	clearErrors?: UseFormClearErrors<ProductType>
	getValues?: UseFormGetValues<ProductType>
	hidePurchasingPeriod?: boolean
	formTemplates?: readonly FormTemplateResponse[]
}

type StringPropertyNames<T> = Exclude<
	{
		[K in keyof T]: T[K] extends string | undefined ? K : never
	}[keyof T],
	undefined
>

interface FormItemProps<TName extends StringPropertyNames<ProductType>> {
	name: TName
	label: React.ReactNode
	dataTestId?: string
	required?: boolean
	multiline?: boolean
	placeholder?: string
	disabled?: boolean
}

// Move logic directly to input components
const FormItem = <TName extends StringPropertyNames<ProductType>>({
	name,
	label,
	dataTestId,
	required,
	multiline,
	placeholder,
	disabled,
}: FormItemProps<TName>) => {
	const { control } = useFormContext<ProductType>()

	const maxLength = multiline ? 2048 : 255

	const renderField = useCallback(
		({
			field: { name, value, onChange },
			fieldState: { error },
		}: {
			field: { name: string; value?: string; onChange: (e: unknown) => void }
			fieldState: ControllerFieldState
		}) => (
			<Input
				width="full"
				variant="outlined"
				label={label}
				placeholder={placeholder}
				disabled={disabled}
				inputProps={{ 'data-testid': dataTestId }}
				value={value}
				onChange={onChange}
				name={name}
				fieldError={error}
				multiline={multiline}
				rows={4}
			/>
		),
		[label, placeholder, disabled, dataTestId, multiline]
	)

	return (
		<Controller<ProductType, TName>
			name={name}
			control={control}
			rules={{ required, maxLength }}
			render={renderField}
		/>
	)
}

const InternalReferenceLabel: React.FC = () => {
	const { t } = useTranslation()
	return (
		<Stack direction="row" gap={1} alignItems="center">
			<Typography variant="body1">
				{t('formField.productRefNumRequired')}
			</Typography>
			<Tooltip title={<ProductReferenceTooltip />}>
				<IconButton>
					<InfoIcon />
				</IconButton>
			</Tooltip>
		</Stack>
	)
}

const EMPTY_ARRAY = [] as const

interface FormTemplateSelectFieldProps {
	value?: string
	onChange: (v: string) => void
	formTemplates: readonly FormTemplateResponse[]
}

const FormTemplateSelectField: React.FC<FormTemplateSelectFieldProps> = ({
	value,
	onChange,
	formTemplates,
}) => {
	const { t } = useTranslation()
	const handleChange = useCallback(
		(e: { target: { value: string } }) => onChange(e.target.value),
		[onChange]
	)
	return (
		<FormControl fullWidth>
			<InputLabel id="form-template-label">
				{t('formField.formTemplate')}
			</InputLabel>
			<Select
				labelId="form-template-label"
				value={value ?? ''}
				onChange={handleChange}
				label={t('formField.formTemplate')}
			>
				<MenuItem value="">{t('formField.formTemplateNone')}</MenuItem>
				{formTemplates.map(template => (
					<MenuItem key={template.id} value={template.id}>
						{template.name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)
}

// TODO(thierry): Handle supplierReference in the form
const ProductForm: React.FC<Props> = ({
	hasRequestCommentField,
	displaySupplierSelection = false,
	control,
	supplierData = EMPTY_ARRAY,
	disabled = false,
	clearErrors,
	getValues,
	hidePurchasingPeriod = false,
	formTemplates,
}) => {
	const { t } = useTranslation()

	const renderTemplateSelect = useCallback(
		({
			field: { value, onChange },
		}: {
			field: { value?: string; onChange: (v: string) => void }
		}) => (
			<FormTemplateSelectField
				value={value}
				onChange={onChange}
				formTemplates={formTemplates ?? EMPTY_ARRAY}
			/>
		),
		[formTemplates]
	)

	return (
		<Stack gap={2} width="100%" maxWidth={800}>
			{displaySupplierSelection && clearErrors && getValues && (
				<SupplierAutocomplete
					control={control}
					supplierData={supplierData}
					disabled={disabled}
					clearErrors={clearErrors}
					getValues={getValues}
				/>
			)}
			{!!formTemplates?.length && (
				<Controller
					name="formTemplateId"
					control={control}
					render={renderTemplateSelect}
				/>
			)}

			<Stack direction="row" gap={2}>
				<Box flex={1}>
					<Box flex={1}>
						<FormItem
							name="internalReference"
							label={<InternalReferenceLabel />}
							placeholder={t('formField.productRefNumRequired')}
							dataTestId="reference-number-input"
							required
						/>
					</Box>
				</Box>
			</Stack>

			<FormItem
				name="name"
				label=""
				placeholder={t('formField.productNameRequired')}
				dataTestId="product-name-input"
				required
			/>

			<FormItem
				name="description"
				label=""
				placeholder={t('formField.productDescription', {
					productOrService: t('word.product'),
				})}
				dataTestId="product-desc-input"
			/>

			<FormUnitSelector
				name="unit"
				required
				dataTestId="product-request-emission-unit"
			/>

			{!hidePurchasingPeriod && (
				<Stack gap={1}>
					<Typography variant="body2">
						{t('formField.purchasingPeriod')}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t('formField.validityPeriodHelperText.request', {
							productOrService: t('word.product').toLowerCase(),
						})}
					</Typography>

					<Stack gap={2} direction="row">
						<DatePickerController
							label={t('formField.purchasingPeriodStartDate')}
							control={control}
							name="purchasingPeriodStart"
						/>
						<DatePickerController
							label={t('formField.purchasingPeriodEndDate')}
							control={control}
							name="purchasingPeriodEnd"
						/>
					</Stack>
				</Stack>
			)}

			{hasRequestCommentField && (
				<>
					<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
						{t('formField.addRequirementsForSuppliers')}
					</Typography>
					<FormItem
						name="requestComment"
						label=""
						placeholder={t('formField.productRequestComment')}
						dataTestId="product-request-comment-input"
						multiline
					/>
				</>
			)}
		</Stack>
	)
}
export { ProductForm, type ProductType }
